import React, { useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const sessionExpired = (location.state as any)?.sessionExpired === true;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!username || !password) {
        setError('Please enter both username and password');
        setIsLoading(false);
        return;
      }

      await login(username, password);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: '#1976d2',
            mb: 2,
          }}
        >
          <LockOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
        </Box>

        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>
          OJRKS Mandir Nirmaan 
        </Typography>

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
          Sign in to access the Mandir Nirmaan portal
        </Typography>

        {sessionExpired && (
          <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
            Your session has expired. Please sign in again.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            fullWidth
            label="Username or Email"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            placeholder="Enter your username or email"
            disabled={isLoading}
            autoFocus
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            placeholder="Enter your password"
            disabled={isLoading}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </Box>



        <Box sx={{ textAlign: 'center', width: '100%', mb: 2, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="textSecondary">
            <Link
              onClick={() => navigate('/forgot-password')}
              sx={{
                color: '#1976d2',
                cursor: 'pointer',
                fontWeight: '600',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Forgot Password?
            </Link>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Don't have an account?{' '}
            <Link
              onClick={() => navigate('/signup')}
              sx={{
                color: '#1976d2',
                cursor: 'pointer',
                fontWeight: '600',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Sign Up
            </Link>
          </Typography>
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
          <Typography variant="body2" color="textSecondary">
            Having trouble? Contact your administrator.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
